// SPDX-License-Identifier: AGPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Check, ChevronLeft, ClipboardList, ShieldCheck } from 'lucide-react';
import {
  Renderer,
  isItemActive,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  translate,
  translateField,
  type Finding,
  type Language,
} from '@intakeform/ui';
import { isHiddenFromPatient, partitionFindings, validate } from '@intakeform/validation';
import { ExtensionUrl, type Questionnaire, type QuestionnaireItem } from '@intakeform/fhir';
import {
  useIntake,
  useSaveDraft,
  useSubmitResponse,
  useVerifyIdentity,
  uploadAttachment,
} from '../../api/intake.js';
import {
  buildQuestionnaireResponse,
  draftAnswersFromResource,
  useIntakeStore,
} from '../../stores/intake.js';
import { useDebouncedAutosave } from '../../hooks/useDebouncedAutosave.js';
import { useLiveValidation } from '../../hooks/useLiveValidation.js';
import { FindingsList } from '../../components/FindingsList.js';
import { StalePromptCard } from '../../components/StalePromptCard.js';
import { IntakeLinkError } from '../../components/IntakeLinkError.js';
import { SkeletonList } from '../../components/Skeleton.js';
import { EmptyState } from '../../components/EmptyState.js';
import { BrandSymbol } from '../../components/BrandMark.js';
import { OrgThemeStyles } from '../../theme/index.js';

const AUTOSAVE_DELAY_MS = 5_000;

function questionnaireRef(q: Questionnaire): string {
  const id = q.id ?? 'unknown';
  const version = q.version ?? '1';
  return `Questionnaire/${id}|${version}`;
}

interface SubmissionSummaryProps {
  questionnaire: Questionnaire;
  answers: Record<string, unknown>;
  lang: Language;
}

// FHIR answer items mix several `value*` keys; we don't model the union
// here because we only read display-shaped fields. `unknown` + property
// access via `in` lets the type checker do its job while keeping the
// renderer permissive against unfamiliar shapes.
type AnswerLike = Record<string, unknown>;
function isAnswerLike(x: unknown): x is AnswerLike {
  return typeof x === 'object' && x !== null;
}

// Exported for unit tests — the review step and confirmation summary both
// funnel every displayed answer through this formatter.
export function formatAnswerValue(val: unknown, lang: Language): string {
  if (val === undefined || val === null) return '';
  const yes = translate(lang, 'fields.yes');
  const no = translate(lang, 'fields.no');

  const formatSingle = (item: unknown): string => {
    if (typeof item === 'boolean') return item ? yes : no;
    if (!isAnswerLike(item)) return String(item);

    // Attachment carriers (signature / file / insurance card) serialize
    // as objects; show the human title instead of raw JSON.
    if (
      typeof item['url'] === 'string' ||
      typeof item['contentType'] === 'string' ||
      '_status' in item
    ) {
      const title = item['title'];
      return typeof title === 'string' && title.trim() !== ''
        ? title
        : translate(lang, 'fields.file-attached');
    }

    const coding = item['valueCoding'];
    if (isAnswerLike(coding)) {
      const display = coding['display'];
      const code = coding['code'];
      return typeof display === 'string' ? display : typeof code === 'string' ? code : '';
    }
    if (typeof item['valueString'] === 'string') return item['valueString'];
    if (typeof item['valueInteger'] === 'number') return String(item['valueInteger']);
    if (typeof item['valueDate'] === 'string') return item['valueDate'];
    if (typeof item['valueTime'] === 'string') return item['valueTime'];
    if (typeof item['valueBoolean'] === 'boolean') return item['valueBoolean'] ? yes : no;
    const ref = item['valueReference'];
    if (isAnswerLike(ref)) {
      const display = ref['display'];
      const reference = ref['reference'];
      return typeof display === 'string' ? display : typeof reference === 'string' ? reference : '';
    }
    if ('code' in item) {
      const display = item['display'];
      const code = item['code'];
      return String(typeof display === 'string' ? display : code);
    }
    return JSON.stringify(item);
  };

  if (Array.isArray(val)) {
    return val
      .map(formatSingle)
      .filter((s) => s !== '')
      .join(', ');
  }
  return formatSingle(val);
}

function SubmissionSummary({ questionnaire, answers, lang }: SubmissionSummaryProps) {
  const t = (key: Parameters<typeof translate>[1], args?: Record<string, string | number>) =>
    translate(lang, key, args);
  const walk = (
    items: readonly QuestionnaireItem[] | undefined,
    groupTitle: string,
  ): ReactNode[] => {
    if (items === undefined) return [];
    const elements: ReactNode[] = [];
    const rows: ReactNode[] = [];

    for (const item of items) {
      if (!isItemActive(item, answers) || isHiddenFromPatient(item)) continue;
      if (item.type === 'group') {
        const childRows = walk(item.item, item.text ?? item.linkId);
        elements.push(...childRows);
        continue;
      }
      if (item.type === 'display') continue;

      const rawVal = answers[item.linkId];
      if (rawVal === undefined || rawVal === null || rawVal === '') continue;

      const formattedVal = formatAnswerValue(rawVal, lang);
      if (formattedVal.trim() === '') continue;

      rows.push(
        <div key={item.linkId} className="summary-grid-row">
          <div className="summary-field-label">{item.text ?? item.linkId}</div>
          <div className="summary-field-value">{formattedVal}</div>
        </div>,
      );
    }

    if (rows.length > 0) {
      elements.push(
        <div key={groupTitle} className="summary-section-card">
          <div className="summary-section-header">{groupTitle}</div>
          <div>{rows}</div>
        </div>,
      );
    }

    return elements;
  };

  const sections = walk(questionnaire.item, 'General Information');

  if (sections.length === 0) {
    return (
      <p className="muted text-center" style={{ padding: '24px 0' }}>
        {t('submit.success.no-answers')}
      </p>
    );
  }

  return <div className="space-y-4">{sections}</div>;
}

export function IntakeRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token) {
    return (
      <IntakeLinkError
        error={{
          status: 400,
          code: 'invalid_token',
          message: 'Missing magic-link token.',
        }}
      />
    );
  }
  return <IntakeView token={token} />;
}

function IntakeView({ token }: { token: string }) {
  // Patient language picker — driven by ?lang= so the choice survives
  // reload and can be shared via URL. Falls back to English on any
  // unsupported / missing code.
  const [searchParams, setSearchParams] = useSearchParams();
  const langParam = searchParams.get('lang');
  const lang: Language = langParam !== null && isSupportedLanguage(langParam) ? langParam : 'en';
  const t = useCallback(
    (key: Parameters<typeof translate>[1], args?: Record<string, string | number>) =>
      translate(lang, key, args),
    [lang],
  );

  const intake = useIntake(token);
  const answers = useIntakeStore((s) => s.answers);
  const dirty = useIntakeStore((s) => s.dirty);
  const confirmedStale = useIntakeStore((s) => s.confirmedStale);
  const lastSavedAt = useIntakeStore((s) => s.lastSavedAt);
  const hydrate = useIntakeStore((s) => s.hydrate);
  const markSaved = useIntakeStore((s) => s.markSaved);
  const setAnswer = useIntakeStore((s) => s.setAnswer);
  const confirmStale = useIntakeStore((s) => s.confirmStale);
  const reset = useIntakeStore((s) => s.reset);

  const saveDraft = useSaveDraft(token);
  const submit = useSubmitResponse(token);
  // EMR demographics arrive only after the patient confirms who they are
  // (intakeform-preview-200.7.9). Nothing from the chart is in the page before
  // this resolves.
  const verifyIdentity = useVerifyIdentity(token);
  const [identityAnswer, setIdentityAnswer] = useState('');
  const [identityFailed, setIdentityFailed] = useState(false);

  const [submitFindings, setSubmitFindings] = useState<Finding[] | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());
  // Per-field "touched" set drives live structural error visibility. A
  // field is touched on first blur OR when Submit is attempted (which
  // promotes the whole form). Sets render-equal by identity, so we
  // always allocate a fresh Set on update.
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  // True once the patient has clicked Submit at least once. When true,
  // every field is treated as touched so latent structural errors are
  // surfaced even on fields the patient never focused.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const markTouched = useCallback((linkId: string) => {
    setTouched((prev) => {
      if (prev.has(linkId)) return prev;
      const next = new Set(prev);
      next.add(linkId);
      return next;
    });
  }, []);

  // Step-wizard state (Clinical Slate, Pass 3): one concept per screen.
  // `welcome` shows the intro + any stale-data prompts; `form` walks the
  // steps; the final step is a synthetic review; `submitted` (below)
  // owns the confirmation. Steps are the active top-level questionnaire
  // items — enableWhen still evaluates against the full answers map, so
  // a conditional top-level step can appear/disappear between screens.
  const [screen, setScreen] = useState<'welcome' | 'form'>('welcome');
  const [stepIdx, setStepIdx] = useState(0);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);

  const steps = useMemo<string[]>(() => {
    if (!intake.data) return [];
    return (intake.data.questionnaire.item ?? [])
      .filter((it) => isItemActive(it, answers) && !isHiddenFromPatient(it))
      .map((it) => it.linkId);
  }, [intake.data, answers]);

  // Keep the cursor in range if a conditional step drops out from under it.
  useEffect(() => {
    setStepIdx((i) => (i > steps.length ? steps.length : i));
  }, [steps.length]);

  // Move focus to the step heading on navigation so screen-reader and
  // keyboard users land on the new concept (matches Pass 3).
  useEffect(() => {
    if (screen === 'form') stepHeadingRef.current?.focus();
  }, [screen, stepIdx]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!intake.data) return;
    const seed: Record<string, unknown> = {};
    for (const [linkId, p] of Object.entries({
      ...intake.data.prefill,
      ...(verifyIdentity.data?.prefill ?? {}),
    })) {
      if (p.value !== undefined && p.value !== null) seed[linkId] = p.value;
    }
    // Cross-session draft restore (intakeform-preview-kva). If the
    // patient previously auto-saved against this magic link, their
    // own answers OVERLAY the prefill — they may have intentionally
    // overwritten a prefilled value.
    if (intake.data.draft !== undefined && intake.data.draft !== null) {
      const draftAnswers = draftAnswersFromResource(intake.data.draft);
      Object.assign(seed, draftAnswers);
    }
    hydrate(seed, {});
  }, [intake.data, verifyIdentity.data, hydrate]);

  useEffect(() => () => reset(), [token, reset]);

  const ref = useMemo(
    () => (intake.data ? questionnaireRef(intake.data.questionnaire) : ''),
    [intake.data],
  );

  useDebouncedAutosave({
    dirty: dirty && !submitted,
    delayMs: AUTOSAVE_DELAY_MS,
    onSave: async () => {
      if (!intake.data || ref === '') return;
      const resource = buildQuestionnaireResponse({
        questionnaireRef: ref,
        status: 'in-progress',
        answers,
        questionnaire: intake.data.questionnaire,
      });
      try {
        const result = await saveDraft.mutateAsync({ resource });
        markSaved(new Date(result.savedAt));
      } catch {
        // surfaced via the mutation; do not throw
      }
    },
  });

  useAttachmentUploader(token, answers, setAnswer);
  useInsuranceCardOcr(token, intake.data?.questionnaire, answers, setAnswer);

  const live = useLiveValidation({
    questionnaire: intake.data?.questionnaire,
    answers,
  });

  // Attachment carriers have a lifecycle independent of the validation
  // engine. A file that's mid-PUT has a value in `answers` (so the
  // engine considers the field answered), but its URL isn't set yet.
  // Block Submit while any attachment is uploading or in error.
  const pendingAttachments = useMemo(() => {
    const pending: { linkId: string; reason: 'uploading' | 'error' }[] = [];
    for (const [linkId, value] of Object.entries(answers)) {
      const carrier =
        value !== null && typeof value === 'object'
          ? (value as { _status?: 'idle' | 'uploading' | 'uploaded' | 'error' })
          : undefined;
      if (carrier?._status === 'uploading') pending.push({ linkId, reason: 'uploading' });
      else if (carrier?._status === 'error') pending.push({ linkId, reason: 'error' });
    }
    return pending;
  }, [answers]);

  // Live structural findings the patient should actually see right now.
  // Gate by per-field `touched` set (or `attemptedSubmit` for "show
  // everything"). After successful submit, server-returned findings
  // supersede live ones.
  const displayedFieldFindings = useMemo<Finding[]>(() => {
    if (submitFindings !== null && submitFindings.length > 0) {
      return submitFindings;
    }
    return live.structural.filter(
      (f) => f.linkId !== undefined && (attemptedSubmit || touched.has(f.linkId)),
    );
  }, [submitFindings, live.structural, touched, attemptedSubmit]);

  // Form-level findings (e.g. `incomplete`) never carry a linkId so the
  // Renderer drops them — surface as a banner near Submit instead.
  // Only show after the patient has tried to submit at least once; the
  // running count in the header already tells them how many are left.
  const displayedFormLevelFindings = useMemo<Finding[]>(() => {
    if (!attemptedSubmit) return [];
    return live.formLevel;
  }, [attemptedSubmit, live.formLevel]);

  // Post-submit clinical-only findings for the FindingsList card. The
  // server returns everything including structural findings; those are
  // already shown inline, so re-rendering them in the summary would be
  // confusing noise. Clinical findings (contradiction, eligibility,
  // async) are the unique value-add of the server pass.
  const submitClinicalFindings = useMemo<Finding[]>(() => {
    if (submitFindings === null) return [];
    return partitionFindings(submitFindings).clinical;
  }, [submitFindings]);

  const saveLabel = useMemo(() => {
    if (saveDraft.isPending) return t('autosave.unsaved');
    if (dirty) return t('autosave.unsaved');
    if (lastSavedAt === undefined) return t('autosave.not-saved');
    const seconds = Math.max(0, Math.round((now - lastSavedAt.getTime()) / 1000));
    if (seconds < 5) return t('autosave.just-now');
    if (seconds < 60) return t('autosave.seconds', { n: seconds });
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return t('autosave.minutes', { n: minutes });
    return t('autosave.hours', { n: Math.round(minutes / 60) });
  }, [saveDraft.isPending, dirty, lastSavedAt, now, t]);

  if (intake.isLoading) {
    return (
      <main className="app-main" id="main-content" tabIndex={-1}>
        <SkeletonList rows={5} ariaLabel={t('loading.form')} />
      </main>
    );
  }
  if (intake.error) {
    return <IntakeLinkError error={intake.error} />;
  }
  if (!intake.data) {
    return <IntakeLinkError error={null} />;
  }

  const itemCount = countAllItems(intake.data.questionnaire.item);
  if (itemCount === 0) {
    return (
      <main className="app-main" id="main-content" tabIndex={-1}>
        <EmptyState
          icon={<ClipboardList size={28} strokeWidth={1.5} aria-hidden="true" />}
          title="No questions yet"
          body={t('empty.form-body')}
          dataTestId="intake-no-items"
        />
      </main>
    );
  }

  const itemsByLinkId = collectItemsByLinkId(intake.data.questionnaire);
  const questionnaire = intake.data.questionnaire;

  // Field linkIds belonging to one step (a top-level item), respecting
  // enableWhen so conditionally-hidden children aren't gated on.
  const collectStepFieldLinkIds = (rootLinkId: string): string[] => {
    const root = itemsByLinkId.get(rootLinkId);
    const out: string[] = [];
    const walk = (it: QuestionnaireItem): void => {
      if (!isItemActive(it, answers) || isHiddenFromPatient(it)) return;
      if (it.type === 'group') {
        for (const child of it.item ?? []) walk(child);
        return;
      }
      if (it.type === 'display') return;
      out.push(it.linkId);
    };
    if (root) walk(root);
    return out;
  };

  const stepLabel = (rootLinkId: string): string => {
    const it = itemsByLinkId.get(rootLinkId);
    if (it === undefined) return rootLinkId;
    return it.text !== undefined ? translateField(it, 'text', lang, it.text) : rootLinkId;
  };

  const summarizeStep = (rootLinkId: string): string =>
    collectStepFieldLinkIds(rootLinkId)
      .map((id) => formatAnswerValue(answers[id], lang))
      .filter((s) => s.trim() !== '')
      .join(' · ');

  // linkId → the step that owns it, so a blocked submit can jump the
  // patient back to the screen carrying the first error.
  const stepIndexByLinkId = new Map<string, number>();
  steps.forEach((rootLinkId, i) => {
    for (const id of collectStepFieldLinkIds(rootLinkId)) stepIndexByLinkId.set(id, i);
  });

  const reviewIdx = steps.length;
  const totalScreens = steps.length + 1;
  const safeIdx = Math.min(stepIdx, reviewIdx);
  const onReview = safeIdx >= reviewIdx;
  const currentRootLinkId = onReview ? undefined : steps[safeIdx];
  const currentRootItem =
    currentRootLinkId !== undefined ? itemsByLinkId.get(currentRootLinkId) : undefined;
  const isDisplayStep = currentRootItem?.type === 'display';
  // Drives which heading to keep: a `group` step shows the big h1 and
  // hides the renderer's top-level <legend>; a `single` field step shows
  // the h1 and hides the field's own (duplicate) label; `display` and
  // `review` just show the h1.
  const stepKind: 'review' | 'display' | 'group' | 'single' = onReview
    ? 'review'
    : isDisplayStep
      ? 'display'
      : currentRootItem?.type === 'group'
        ? 'group'
        : 'single';

  const stalePrompts = Object.entries(intake.data.prefill).filter(
    ([linkId, p]) => p.stale && !confirmedStale.has(linkId),
  );

  const onSubmit = async (): Promise<void> => {
    if (!intake.data) return;
    setAttemptedSubmit(true);
    // Block submission while any attachment is still uploading or in
    // error — the response would otherwise serialize without the URL.
    if (pendingAttachments.length > 0) return;
    // Build the response we're about to POST and validate it
    // *synchronously* over the latest answers. The `live` hook uses
    // useDeferredValue, so its `ok` flag can lag a keystroke or two
    // behind reality. The Submit decision must use a fresh result.
    const resource = buildQuestionnaireResponse({
      questionnaireRef: ref,
      status: 'completed',
      answers,
      questionnaire,
    });
    const freshResult = validate({
      questionnaire,
      response: resource,
      now: new Date(),
    });
    const freshPartitioned = partitionFindings(freshResult.findings);
    const blockingStructural = [
      ...freshPartitioned.structural,
      ...freshPartitioned.formLevel,
    ].filter((f) => f.severity === 'error');
    if (blockingStructural.length > 0) {
      // Send the patient back to the step carrying the first error so the
      // field is actually on screen, then scroll to it.
      const firstLinkId = blockingStructural.find((f) => f.linkId !== undefined)?.linkId;
      if (firstLinkId !== undefined) {
        const targetStep = stepIndexByLinkId.get(firstLinkId);
        if (targetStep !== undefined) setStepIdx(targetStep);
        setTimeout(() => {
          document
            .querySelector(`[data-field-host="true"][data-link-id="${firstLinkId}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
      }
      return;
    }
    try {
      const result = await submit.mutateAsync({ resource });
      setSubmitFindings(result.findings);
      setSubmitted(true);
    } catch {
      // surfaced via the mutation
    }
  };

  // Advance one step, gating on this step's own structural errors.
  const goNext = (): void => {
    if (currentRootLinkId === undefined) return;
    const ids = collectStepFieldLinkIds(currentRootLinkId);
    // Promote this step's fields to "touched" so their errors surface.
    setTouched((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    const resource = buildQuestionnaireResponse({
      questionnaireRef: ref,
      status: 'in-progress',
      answers,
      questionnaire,
    });
    const res = validate({ questionnaire, response: resource, now: new Date() });
    const allow = new Set(ids);
    const blocking = res.findings.filter(
      (f) => f.severity === 'error' && f.linkId !== undefined && allow.has(f.linkId),
    );
    if (blocking.length > 0) {
      const firstLinkId = blocking[0]?.linkId;
      if (firstLinkId !== undefined) {
        document
          .querySelector(`[data-field-host="true"][data-link-id="${firstLinkId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setStepIdx(Math.min(safeIdx + 1, reviewIdx));
  };

  const goBack = (): void => {
    if (safeIdx === 0) {
      setScreen('welcome');
      return;
    }
    setStepIdx(Math.max(0, safeIdx - 1));
  };

  // "Save and finish later" — flush a draft now, then return to the
  // welcome screen (the patient can re-open the magic link to resume).
  const finishLater = (): void => {
    if (ref !== '') {
      const resource = buildQuestionnaireResponse({
        questionnaireRef: ref,
        status: 'in-progress',
        answers,
        questionnaire,
      });
      void saveDraft
        .mutateAsync({ resource })
        .then((r) => markSaved(new Date(r.savedAt)))
        .catch(() => {
          // surfaced via the mutation
        });
    }
    setScreen('welcome');
  };

  const setLanguage = (code: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set('lang', code);
    setSearchParams(next, { replace: true });
  };

  const langSwitch = (
    <div className="intake-langseg" role="group" aria-label={t('lang.picker-label')}>
      {SUPPORTED_LANGUAGES.map((l) => (
        <button
          key={l.code}
          type="button"
          className="intake-langseg-btn"
          aria-pressed={lang === l.code}
          onClick={() => setLanguage(l.code)}
          data-language-option={l.code}
        >
          {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  const proxyBanner =
    intake.data.filler !== undefined && intake.data.subject !== undefined ? (
      <div
        className="proxy-banner"
        role="status"
        aria-live="polite"
        data-proxy-banner="true"
        data-relationship={intake.data.filler.relationshipCode}
      >
        You’re filling out this form as <strong>{intake.data.filler.displayName}</strong> for{' '}
        <strong>{intake.data.subject.displayName}</strong> (
        {intake.data.filler.relationshipDisplay.toLowerCase()}).
      </div>
    ) : null;

  const startLabel =
    intake.data.draft !== undefined && intake.data.draft !== null
      ? t('wizard.resume')
      : t('wizard.start');

  return (
    <>
      <OrgThemeStyles settings={intake.data.organizationSettings} />
      <div className="app-shell intake-shell" data-intake-wizard="true">
        <a className="skip-link" href="#main-content">
          Skip to main content
        </a>

        {submitted ? (
          <main className="intake-screen" id="main-content" tabIndex={-1} lang={lang}>
            <div className="intake-donewrap">
              <div className="intake-donecheck" aria-hidden="true">
                <Check size={34} strokeWidth={2.2} />
              </div>
              <h1 ref={stepHeadingRef} tabIndex={-1} className="intake-h1 intake-center">
                {t('submit.success.heading')}
              </h1>
              <p className="intake-lead intake-center">{t('submit.success.body')}</p>

              <h2 className="section-title">{t('wizard.confirm.next-heading')}</h2>
              <ul className="intake-nextlist">
                <li data-ui-card="">
                  <h3 className="intake-next-h">{t('wizard.confirm.next-when-h')}</h3>
                  <p className="intake-next-b">{t('wizard.confirm.next-when-b')}</p>
                </li>
                <li data-ui-card="">
                  <h3 className="intake-next-h">{t('wizard.confirm.next-who-h')}</h3>
                  <p className="intake-next-b">{t('wizard.confirm.next-who-b')}</p>
                </li>
                <li data-ui-card="">
                  <h3 className="intake-next-h">{t('wizard.confirm.next-fix-h')}</h3>
                  <p className="intake-next-b">{t('wizard.confirm.next-fix-b')}</p>
                </li>
              </ul>

              {submitClinicalFindings.length > 0 ? (
                <FindingsList
                  findings={submitClinicalFindings}
                  title={t('findings.clinical-heading')}
                />
              ) : null}

              <h2 className="section-title">{t('submit.success.summary')}</h2>
              <SubmissionSummary questionnaire={questionnaire} answers={answers} lang={lang} />
            </div>
          </main>
        ) : screen === 'welcome' ? (
          <main className="intake-screen" id="main-content" tabIndex={-1} lang={lang}>
            <div className="intake-wbar">
              <span className="brand-link">
                <BrandSymbol
                  className="brand-symbol"
                  aria-hidden="true"
                  focusable="false"
                  style={{ width: 24, height: 24 }}
                />
                <span>{questionnaire.title ?? 'Intake form'}</span>
              </span>
              {langSwitch}
            </div>
            <div className="intake-wbody">
              <p className="intake-secure">
                <ShieldCheck size={16} strokeWidth={1.5} aria-hidden="true" />
                <span>{t('wizard.secure')}</span>
              </p>
              <h1 ref={stepHeadingRef} tabIndex={-1} className="intake-h1">
                {questionnaire.title ?? 'Intake form'}
              </h1>
              <p className="intake-lead">{t('wizard.welcome.body')}</p>
              {proxyBanner}
              {intake.data.emrPrefill?.state === 'needs_identity' &&
              verifyIdentity.data === undefined ? (
                /*
                  Asks for something we have NOT shown. If this link reached
                  the wrong person, they cannot answer, and the form stays
                  empty instead of quietly rendering someone else's record
                  (intakeform-preview-200.7.9). Getting it wrong costs typing,
                  never access: the form below works either way.
                */
                <section aria-labelledby="identity-heading" className="stale-section">
                  <h2 id="identity-heading" className="section-title">
                    Confirm your date of birth
                  </h2>
                  <p className="intake-lead">
                    Confirm the patient&apos;s date of birth and we will fill in the details your
                    clinic already holds. You can skip this and fill the form in yourself.
                  </p>
                  <div className="max-w-xs space-y-2">
                    <label htmlFor="identity-dob" className="text-sm font-medium">
                      Date of birth
                    </label>
                    <input
                      id="identity-dob"
                      type="date"
                      value={identityAnswer}
                      onChange={(e) => setIdentityAnswer(e.currentTarget.value)}
                      className="w-full rounded-md border border-border bg-surface p-2"
                      data-testid="intake-identity-dob"
                    />
                    <button
                      data-ui-button=""
                      data-variant="secondary"
                      type="button"
                      disabled={identityAnswer === '' || verifyIdentity.isPending}
                      data-testid="intake-identity-submit"
                      onClick={() => {
                        setIdentityFailed(false);
                        verifyIdentity.mutate(
                          { birthDate: identityAnswer },
                          { onError: () => setIdentityFailed(true) },
                        );
                      }}
                    >
                      {verifyIdentity.isPending ? 'Checking…' : 'Confirm'}
                    </button>
                    {identityFailed ? (
                      <p className="error-banner" role="status" data-testid="intake-identity-error">
                        That does not match our records. You can still fill in the form yourself.
                      </p>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {stalePrompts.length > 0 ? (
                <section aria-labelledby="verify-existing-heading" className="stale-section">
                  <h2 id="verify-existing-heading" className="section-title">
                    {t('findings.stale-heading')}
                  </h2>
                  <div className="stale-card-grid">
                    {stalePrompts.map(([linkId, p]) => (
                      <StalePromptCard
                        key={linkId}
                        linkId={linkId}
                        label={itemsByLinkId.get(linkId)?.text ?? linkId}
                        item={p}
                        now={now}
                        onConfirm={() => confirmStale(linkId)}
                        onEdit={() => {
                          setAnswer(linkId, undefined);
                          confirmStale(linkId);
                        }}
                      />
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
            <footer className="intake-foot">
              <button
                data-ui-button=""
                data-variant="primary"
                data-size="lg"
                data-intake-start="true"
                onClick={() => {
                  setStepIdx(0);
                  setScreen('form');
                }}
              >
                {startLabel}
              </button>
            </footer>
          </main>
        ) : (
          <main className="intake-screen" id="main-content" tabIndex={-1} lang={lang}>
            <nav
              className="intake-topbar"
              aria-label={t('wizard.step-of', { current: safeIdx + 1, total: totalScreens })}
            >
              <button
                type="button"
                className="intake-iconbtn"
                onClick={goBack}
                aria-label={t('wizard.back')}
                data-intake-back="true"
              >
                <ChevronLeft size={20} strokeWidth={1.8} aria-hidden="true" />
              </button>
              <div className="intake-prog">
                <div className="intake-prog-row">
                  <span>{t('wizard.step-of', { current: safeIdx + 1, total: totalScreens })}</span>
                </div>
                <div
                  className="intake-prog-track"
                  role="progressbar"
                  aria-valuenow={safeIdx + 1}
                  aria-valuemin={1}
                  aria-valuemax={totalScreens}
                  aria-label={t('wizard.step-of', { current: safeIdx + 1, total: totalScreens })}
                >
                  <div
                    className="intake-prog-fill"
                    style={{ transform: `scaleX(${(safeIdx + 1) / totalScreens})` }}
                  />
                </div>
              </div>
              {langSwitch}
            </nav>

            <div className="intake-savebar" aria-live="polite">
              <span
                className="intake-savebar-in"
                data-autosave-state={saveDraft.isPending ? 'saving' : dirty ? 'dirty' : 'saved'}
              >
                {saveDraft.isPending ? (
                  <>
                    <span className="intake-spin" aria-hidden="true" />
                    {t('autosave.unsaved')}
                  </>
                ) : (
                  <>
                    <span data-dot className="intake-okdot" aria-hidden="true" />
                    {saveLabel}
                  </>
                )}
              </span>
            </div>

            <div className="intake-stepbody" data-step-kind={stepKind}>
              {onReview ? (
                <>
                  <h1 ref={stepHeadingRef} tabIndex={-1} className="intake-h1">
                    {t('wizard.review.title')}
                  </h1>
                  <p className="intake-help">{t('wizard.review.help')}</p>
                  <ul className="intake-reviewlist">
                    {steps.map((rootLinkId, i) => {
                      const val = summarizeStep(rootLinkId);
                      return (
                        <li key={rootLinkId} className="intake-rev">
                          <div className="intake-rev-main">
                            <span className="intake-rev-label">{stepLabel(rootLinkId)}</span>
                            <span className={'intake-rev-val' + (val ? '' : ' is-empty')}>
                              {val || t('wizard.review.not-answered')}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="intake-rev-edit"
                            onClick={() => setStepIdx(i)}
                          >
                            {t('wizard.review.edit')}
                          </button>
                        </li>
                      );
                    })}
                  </ul>

                  {displayedFormLevelFindings.length > 0 ? (
                    <div className="error-banner" role="alert" data-form-level-findings="true">
                      {displayedFormLevelFindings.map((f, i) => (
                        <p key={`${f.code}-${String(i)}`} style={{ margin: 0 }}>
                          {f.message}
                        </p>
                      ))}
                    </div>
                  ) : null}

                  {pendingAttachments.length > 0 && attemptedSubmit ? (
                    <div className="error-banner" role="alert" data-attachment-pending="true">
                      {pendingAttachments.some((p) => p.reason === 'error')
                        ? t('attachments.failed')
                        : t('attachments.pending')}
                    </div>
                  ) : null}

                  {submit.error ? (
                    <p className="error-banner" role="alert">
                      {submit.error.message}
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <h1 ref={stepHeadingRef} tabIndex={-1} className="intake-h1">
                    {currentRootLinkId !== undefined ? stepLabel(currentRootLinkId) : ''}
                  </h1>
                  {currentRootLinkId !== undefined && !isDisplayStep ? (
                    <Renderer
                      questionnaire={questionnaire}
                      answers={answers}
                      onChange={(linkId, value) => setAnswer(linkId, value)}
                      findings={displayedFieldFindings}
                      onTouched={markTouched}
                      language={lang}
                      visibleRootLinkIds={[currentRootLinkId]}
                      hideFromPatient
                    />
                  ) : null}
                </>
              )}
            </div>

            <footer className="intake-foot">
              {onReview ? (
                <button
                  data-ui-button=""
                  data-variant="primary"
                  data-size="lg"
                  className="intake-submit-button"
                  onClick={() => {
                    void onSubmit();
                  }}
                  disabled={submit.isPending}
                  data-submit-blocked={
                    attemptedSubmit && (!live.ok || pendingAttachments.length > 0)
                      ? 'true'
                      : 'false'
                  }
                >
                  {submit.isPending ? t('submit.pending') : t('wizard.send')}
                </button>
              ) : (
                <button
                  data-ui-button=""
                  data-variant="primary"
                  data-size="lg"
                  data-intake-continue="true"
                  onClick={goNext}
                >
                  {t('wizard.continue')}
                </button>
              )}
              <button type="button" className="intake-laterlink" onClick={finishLater}>
                {t('wizard.finish-later')}
              </button>
            </footer>
          </main>
        )}
      </div>
    </>
  );
}

function countAllItems(items: readonly QuestionnaireItem[] | undefined): number {
  if (items === undefined) return 0;
  let n = 0;
  for (const item of items) {
    if (item.type === 'group') {
      n += countAllItems(item.item);
    } else if (item.type !== 'display') {
      n += 1;
    }
  }
  return n;
}

function collectItemsByLinkId(q: Questionnaire): Map<string, QuestionnaireItem> {
  const out = new Map<string, QuestionnaireItem>();
  const walk = (items: readonly QuestionnaireItem[] | undefined): void => {
    if (items === undefined) return;
    for (const item of items) {
      out.set(item.linkId, item);
      if (item.type === 'group') walk(item.item);
    }
  };
  walk(q.item);
  return out;
}

interface UploadCarrier {
  _file?: File;
  _status?: 'idle' | 'uploading' | 'uploaded' | 'error';
  _progress?: number;
  _error?: string;
  /**
   * Server-assigned attachment id once the PUT completes. Populated
   * by the uploader so downstream consumers (insurance-card OCR
   * hook, audit trail) can reference the row without re-parsing the
   * `url` field.
   */
  _attachmentId?: string;
  /**
   * Set by the insurance-card OCR hook (bead b02) when the parse
   * returned below the confidence threshold. The FileUpload renderer
   * shows an inline "please verify" warning when this is true so the
   * patient checks the auto-filled fields before submitting.
   */
  _ocrLowConfidence?: boolean;
  contentType?: string;
  size?: number;
  title?: string;
}

/**
 * Below this confidence the OCR pass still prefills the linked
 * items but the renderer also surfaces a "please verify" warning
 * (bead b02). 0.7 is the inflection point Tesseract returns for
 * normally-lit phone shots of cards; below that the field
 * extraction heuristics are uncomfortable and the patient should
 * double-check.
 */
const OCR_LOW_CONFIDENCE_THRESHOLD = 0.7;

function asUploadCarrier(v: unknown): UploadCarrier | undefined {
  return v !== null && typeof v === 'object' ? (v as UploadCarrier) : undefined;
}

/**
 * Watches the answers map and kicks off the presigned-PUT flow for any
 * attachment value that is freshly picked or marked for retry. Status
 * is written back through `setAnswer` so the FileUpload renderer can
 * surface progress + retry without owning the upload mechanism.
 */
function useAttachmentUploader(
  token: string,
  answers: Record<string, unknown>,
  setAnswer: (linkId: string, value: unknown) => void,
): void {
  // Track which (linkId, File) pairs we've already attempted, so the
  // effect doesn't loop on the very `setAnswer` calls it makes.
  const inFlight = useRef<Map<string, File>>(new Map());

  useEffect(() => {
    for (const [linkId, value] of Object.entries(answers)) {
      const carrier = asUploadCarrier(value);
      if (carrier === undefined) continue;
      const file = carrier._file;
      if (file === undefined) continue;
      if (carrier._status === 'uploaded') continue;
      const last = inFlight.current.get(linkId);
      if (last === file && carrier._status === 'uploading') continue;
      if (carrier._status !== 'uploading') continue;
      inFlight.current.set(linkId, file);

      const meta = {
        linkId,
        kind: 'file' as const,
        contentType: file.type === '' ? 'application/octet-stream' : file.type,
        sizeBytes: file.size,
      };
      // Surface a brief "0% → 60%" progress before the PUT resolves.
      // The real Fetch API doesn't expose progress on a PUT; we treat
      // 60% as "request sent, awaiting ack" and snap to 100% on success.
      setAnswer(linkId, { ...carrier, _status: 'uploading', _progress: 5 });
      const tick = setTimeout(() => {
        const cur = asUploadCarrier(answers[linkId]);
        if (cur?._status === 'uploading' && cur._file === file) {
          setAnswer(linkId, { ...cur, _progress: 60 });
        }
      }, 250);

      uploadAttachment(token, meta, file)
        .then((result) => {
          clearTimeout(tick);
          setAnswer(linkId, {
            ...carrier,
            _status: 'uploaded' as const,
            _progress: 100,
            url: result.url,
            _attachmentId: result.attachmentId,
          });
          inFlight.current.delete(linkId);
        })
        .catch((err: unknown) => {
          clearTimeout(tick);
          const message = err instanceof Error ? err.message : 'Upload failed';
          setAnswer(linkId, {
            ...carrier,
            _status: 'error' as const,
            _progress: 0,
            _error: message,
          });
          inFlight.current.delete(linkId);
        });
    }
    // We deliberately track `answers` so retries (which rewrite the
    // carrier with `_status: 'uploading'`) re-enter this effect. The
    // `inFlight` Map debounces against duplicate attempts.
  }, [token, answers, setAnswer]);
}

/**
 * Watches for newly-uploaded insurance-card attachments and triggers
 * OCR on each one (bead 2pc). For every `attachment` item carrying
 * `ExtensionUrl.insuranceCard`, once the upload completes the hook
 * calls POST /api/intake/:token/insurance/parse-card and applies the
 * parsed payor / memberId / groupNumber into the items immediately
 * following the card in document order whose `insuranceCardTarget`
 * extension names the matching field.
 *
 * Document-order ownership avoids needing a back-reference linkId
 * field on every dependent — clinicians just put the card upload
 * BEFORE the related items in the form. The hook re-scans on every
 * answer change but the `parsed` set debounces so we don't re-OCR
 * the same attachment.
 */
function useInsuranceCardOcr(
  token: string,
  questionnaire: Questionnaire | undefined,
  answers: Record<string, unknown>,
  setAnswer: (linkId: string, value: unknown) => void,
): void {
  // Track which attachmentIds we've already OCR'd so the effect
  // doesn't re-fire after we write the parsed values back into the
  // answers map.
  const parsed = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (questionnaire === undefined) return;
    const groups = collectInsuranceCardGroups(questionnaire);
    for (const group of groups) {
      const carrier = asUploadCarrier(answers[group.cardLinkId]);
      if (carrier?._status !== 'uploaded') continue;
      const attachmentId = carrier._attachmentId;
      if (attachmentId === undefined) continue;
      if (parsed.current.has(attachmentId)) continue;
      parsed.current.add(attachmentId);
      const cardLinkId = group.cardLinkId;
      const baseCarrier = carrier;
      void parseAndApply(token, attachmentId, group.targets, setAnswer, (confidence) => {
        // Bead b02: when confidence is below threshold, set the
        // low-confidence flag on the card carrier so the FileUpload
        // renderer shows the "please verify" warning.
        const lowConfidence = confidence < OCR_LOW_CONFIDENCE_THRESHOLD;
        if (lowConfidence) {
          setAnswer(cardLinkId, { ...baseCarrier, _ocrLowConfidence: true });
        }
      });
    }
  }, [token, questionnaire, answers, setAnswer]);
}

interface InsuranceCardGroup {
  cardLinkId: string;
  /** linkId → which parsed field populates it. */
  targets: Map<string, 'payor' | 'memberId' | 'groupNumber'>;
}

const TARGET_FIELDS: ReadonlySet<string> = new Set(['payor', 'memberId', 'groupNumber']);

function collectInsuranceCardGroups(q: Questionnaire): InsuranceCardGroup[] {
  // Walk items in document order. When we hit an attachment item with
  // ExtensionUrl.insuranceCard, open a new group; subsequent items
  // with ExtensionUrl.insuranceCardTarget are owned by that group
  // until the next insurance-card item.
  const flat: QuestionnaireItem[] = [];
  const walk = (items: readonly QuestionnaireItem[] | undefined): void => {
    if (items === undefined) return;
    for (const item of items) {
      flat.push(item);
      if (item.type === 'group') walk(item.item);
    }
  };
  walk(q.item);

  const groups: InsuranceCardGroup[] = [];
  let current: InsuranceCardGroup | undefined;
  for (const item of flat) {
    if (item.type === 'attachment' && hasExt(item, ExtensionUrl.insuranceCard, true)) {
      current = { cardLinkId: item.linkId, targets: new Map() };
      groups.push(current);
      continue;
    }
    if (current === undefined) continue;
    const target = readTargetField(item);
    if (target !== undefined) current.targets.set(item.linkId, target);
  }
  return groups;
}

function hasExt(item: QuestionnaireItem, url: string, expected: boolean): boolean {
  const ext = item.extension?.find((e) => e.url === url);
  return ext?.valueBoolean === expected;
}

function readTargetField(
  item: QuestionnaireItem,
): 'payor' | 'memberId' | 'groupNumber' | undefined {
  const ext = item.extension?.find((e) => e.url === ExtensionUrl.insuranceCardTarget);
  const code = ext?.valueCode;
  if (typeof code !== 'string') return undefined;
  return TARGET_FIELDS.has(code) ? (code as 'payor' | 'memberId' | 'groupNumber') : undefined;
}

async function parseAndApply(
  token: string,
  attachmentId: string,
  targets: Map<string, 'payor' | 'memberId' | 'groupNumber'>,
  setAnswer: (linkId: string, value: unknown) => void,
  onParsed?: (confidence: number) => void,
): Promise<void> {
  if (targets.size === 0) return;
  const res = await fetch(`/api/intake/${encodeURIComponent(token)}/insurance/parse-card`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ attachmentId }),
    credentials: 'include',
  });
  if (!res.ok) return;
  const body = (await res.json()) as {
    parse: {
      payor: string | null;
      memberId: string | null;
      groupNumber: string | null;
      confidence: number;
    };
  };
  for (const [linkId, field] of targets) {
    const value = body.parse[field];
    if (typeof value === 'string' && value.length > 0) {
      setAnswer(linkId, value);
    }
  }
  onParsed?.(body.parse.confidence);
}
